/**
 * Chart Color Utilities
 *
 * Provides chart colors using CSS custom properties and brand tokens
 * instead of hardcoded hex values. This ensures consistency with the
 * design system and allows for theme customization.
 */

import {
  BRAND_CHART_COLORS,
  WARM_CHART_COLORS,
  getChartColors,
} from "../constants/brandColors";

/**
 * Gets a CSS custom property value from the document
 * @param propertyName - The CSS custom property name (with or without --)
 * @returns The computed CSS custom property value or fallback
 */
function getCSSCustomProperty(propertyName: string): string {
  const prop = propertyName.startsWith("--")
    ? propertyName
    : `--${propertyName}`;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(prop)
    .trim();

  // If CSS property is not found, use our fallback mapping
  if (!value) {
    const fallback = FALLBACK_COLORS[prop as keyof typeof FALLBACK_COLORS];
    if (fallback) {
      console.warn(
        `CSS custom property ${prop} not found, using fallback color: ${fallback}`
      );
      return fallback;
    }
    console.warn(
      `CSS custom property ${prop} not found and no fallback available, using default blue`
    );
    return "#3052D8"; // Final fallback
  }

  return value;
}

/**
 * Fallback color mapping for when CSS custom properties are not available
 * These should match the values defined in base.scss
 * Updated to use blue-based palette from vue.frontends
 */
const FALLBACK_COLORS = {
  "--chart-primary": "#406A94", // Steel Blue
  "--chart-secondary": "#7D8CA3", // Cool Gray
  "--chart-tertiary": "#969A97", // Battleship Gray
  "--chart-quaternary": "#334139", // Black Olive
  "--chart-onyx": "#3C3C3C", // Onyx
  "--chart-positive": "#39a275", // Green from vue.frontends
  "--chart-negative": "#dc2626", // Red
  "--chart-neutral": "#7F8C98", // Cadet Gray
  "--chart-warning": "#ea580c", // Orange
  "--chart-purple": "#7c3aed",
  "--chart-cyan": "#0891b2",
  "--chart-lime": "#65a30d",
  "--chart-dark-orange": "#c2410c",
  "--chart-brown": "#7c2d12",
  "--chart-indigo": "#4338ca",
  "--chart-pink": "#be185d",
  "--chart-vanilla": "#eae2b7", // New brand color
} as const;

/**
 * Chart color palette using CSS custom properties
 * These map to the CSS tokens defined in base.scss
 */
export const CHART_CSS_COLORS = {
  primary: "var(--chart-primary)",
  secondary: "var(--chart-secondary)",
  tertiary: "var(--chart-tertiary)",
  quaternary: "var(--chart-quaternary)",
  positive: "var(--chart-positive)",
  negative: "var(--chart-negative)",
  neutral: "var(--chart-neutral)",
  warning: "var(--chart-warning)",
  purple: "var(--chart-purple)",
  cyan: "var(--chart-cyan)",
  lime: "var(--chart-lime)",
  darkOrange: "var(--chart-dark-orange)",
  brown: "var(--chart-brown)",
  indigo: "var(--chart-indigo)",
  pink: "var(--chart-pink)",
  vanilla: "var(--chart-vanilla)",
} as const;

/**
 * Gets an array of chart colors for use in Chart.js datasets
 * @param count - Number of colors needed (optional, returns all if not specified)
 * @param useWarmPalette - Whether to use the warm color palette instead of default
 * @returns Array of CSS color strings
 */
export function getChartColorArray(
  count?: number,
  useWarmPalette = false
): string[] {
  const baseColors = useWarmPalette
    ? Object.values(CHART_CSS_COLORS).slice(0, 7)
    : Object.values(CHART_CSS_COLORS);

  if (count && count > 0) {
    return baseColors.slice(0, count);
  }

  return baseColors;
}

/**
 * Gets chart colors with transparency for background fills
 * @param colors - Array of CSS color strings or resolved hex values
 * @param alpha - Alpha transparency value (0-1)
 * @returns Array of rgba color strings
 */
export function getChartColorsWithAlpha(
  colors: string[],
  alpha = 0.2
): string[] {
  return colors.map((color) => {
    let resolvedColor = color;

    // If it's a CSS variable, resolve it first
    if (color.startsWith("var(")) {
      const propertyName = color.slice(4, -1);
      resolvedColor = getCSSCustomProperty(propertyName);
    }

    // Convert hex to rgba
    if (resolvedColor.startsWith("#")) {
      const hex = resolvedColor.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // If it's already rgba or other format, return as-is
    return resolvedColor;
  });
}

/**
 * Predefined color sets for common chart types
 */
export const CHART_COLOR_SETS = {
  /** Primary set for most dashboard charts */
  primary: [
    CHART_CSS_COLORS.primary,
    CHART_CSS_COLORS.secondary,
    CHART_CSS_COLORS.tertiary,
    CHART_CSS_COLORS.warning,
    CHART_CSS_COLORS.positive,
  ],

  /** Binary comparisons (opt-ins vs opt-outs, accepts vs rejects) */
  binary: [CHART_CSS_COLORS.primary, CHART_CSS_COLORS.secondary],

  /** Performance trends (positive, negative, neutral) */
  performance: [
    CHART_CSS_COLORS.positive,
    CHART_CSS_COLORS.negative,
    CHART_CSS_COLORS.neutral,
  ],

  /** Device breakdown charts */
  devices: [
    CHART_CSS_COLORS.primary,
    CHART_CSS_COLORS.secondary,
    CHART_CSS_COLORS.tertiary,
  ],

  /** Extended set for complex charts with many series */
  extended: [
    CHART_CSS_COLORS.primary,
    CHART_CSS_COLORS.secondary,
    CHART_CSS_COLORS.tertiary,
    CHART_CSS_COLORS.warning,
    CHART_CSS_COLORS.positive,
    CHART_CSS_COLORS.purple,
    CHART_CSS_COLORS.cyan,
    CHART_CSS_COLORS.lime,
  ],
} as const;

/**
 * Gets specific color set by name with resolved CSS custom property values
 * @param setName - Name of the color set
 * @returns Array of resolved color strings (hex values)
 */
export function getChartColorSet(
  setName: keyof typeof CHART_COLOR_SETS
): string[] {
  const cssColors = CHART_COLOR_SETS[setName];

  // Resolve CSS custom properties to actual color values for Chart.js compatibility
  return cssColors.map((cssColor) => {
    if (cssColor.startsWith("var(")) {
      // Extract the custom property name from var(--property-name)
      const propertyName = cssColor.slice(4, -1);
      return getCSSCustomProperty(propertyName);
    }
    return cssColor;
  });
}

/**
 * Creates a Chart.js compatible dataset color configuration
 * @param datasetConfig - Configuration for the dataset colors
 * @returns Object with backgroundColor and borderColor properties
 */
export function createDatasetColors(datasetConfig: {
  colors: string[];
  backgroundAlpha?: number;
  borderAlpha?: number;
  single?: boolean; // For single-color datasets
}) {
  const {
    colors,
    backgroundAlpha = 0.2,
    borderAlpha = 1,
    single = false,
  } = datasetConfig;

  if (single) {
    return {
      backgroundColor:
        backgroundAlpha < 1
          ? getChartColorsWithAlpha([colors[0]], backgroundAlpha)[0]
          : colors[0],
      borderColor:
        borderAlpha < 1
          ? getChartColorsWithAlpha([colors[0]], borderAlpha)[0]
          : colors[0],
    };
  }

  return {
    backgroundColor:
      backgroundAlpha < 1
        ? getChartColorsWithAlpha(colors, backgroundAlpha)
        : colors,
    borderColor:
      borderAlpha < 1 ? getChartColorsWithAlpha(colors, borderAlpha) : colors,
  };
}

/**
 * Debug function to test color resolution
 * @returns Object with resolved colors for debugging
 */
export function debugChartColors() {
  const primarySet = getChartColorSet("primary");
  const binarySet = getChartColorSet("binary");
  const extendedSet = getChartColorSet("extended");

  console.log("Chart Colors Debug:");
  console.log("Primary set:", primarySet);
  console.log("Binary set:", binarySet);
  console.log("Extended set:", extendedSet);

  return {
    primary: primarySet,
    binary: binarySet,
    extended: extendedSet,
  };
}
